import { TextField } from '@mui/material';
import React from 'react';

import { PartActionSelection, SelectedSerial, SelectedPartType } from './MobilePartActionsDialog.props';
import {
  ConfirmContent,
  ErrorMessage,
  NotesSection,
  SelectedActionBadge,
  SerialSection,
  SerialLabel,
  SelectedSerialChip,
  SerialInfo,
  SerialNumber,
} from './MobilePartActionsDialog.styles';
import { OUT_SERIAL_ACTIONS, IN_SERIAL_ACTIONS } from './partActionBuildHelpers';
import { PartSerialSelector } from './PartSerialSelector';
import { PartTypeActionLiveSearchItem } from '../../../../../../@types/partType.type';

interface PartActionConfirmStepProps {
  selectedAction: PartActionSelection;
  error: string | null;
  outSerial: SelectedSerial | null;
  inSerial: SelectedSerial | null;
  selectedPartType?: SelectedPartType | null;
  notes: string;
  onNotesChange: (value: string) => void;
  onOutSelect: (item: PartTypeActionLiveSearchItem | null) => void;
  onInSelect: (item: PartTypeActionLiveSearchItem | null) => void;
  onOutClear: () => void;
  onInClear: () => void;
  onOutSearch: (value: string) => Promise<PartTypeActionLiveSearchItem[]>;
  onInSearch: (value: string) => Promise<PartTypeActionLiveSearchItem[]>;
  isSearching: boolean;
  onCreateOutExceptionally?: (serial: string) => void;
  onCreateInExceptionally?: (serial: string) => void;
  onPartTypeClick?: () => void;
  onActionClick?: () => void;
}

export const PartActionConfirmStep: React.FC<PartActionConfirmStepProps> = ({
  selectedAction,
  error,
  outSerial,
  inSerial,
  selectedPartType,
  notes,
  onNotesChange,
  onOutSelect,
  onInSelect,
  onOutClear,
  onInClear,
  onOutSearch,
  onInSearch,
  isSearching,
  onCreateOutExceptionally,
  onCreateInExceptionally,
  onPartTypeClick,
  onActionClick,
}) => {
  const needsOut = OUT_SERIAL_ACTIONS.includes(selectedAction.code);
  const needsIn = IN_SERIAL_ACTIONS.includes(selectedAction.code);
  const hasPartType = !needsOut && !needsIn && selectedPartType;

  return (
    <ConfirmContent>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      <SelectedActionBadge
        onClick={onActionClick}
        role={onActionClick ? 'button' : undefined}
        tabIndex={onActionClick ? 0 : undefined}
        onKeyDown={onActionClick ? (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onActionClick();
          }
        } : undefined}
        sx={onActionClick ? { cursor: 'pointer' } : undefined}
      >
        {selectedAction.name}
      </SelectedActionBadge>

      {hasPartType && (
        <SerialSection>
          <SerialLabel>Part Type</SerialLabel>
          <SelectedSerialChip
            onClick={onPartTypeClick}
            role={onPartTypeClick ? 'button' : undefined}
            tabIndex={onPartTypeClick ? 0 : undefined}
            onKeyDown={onPartTypeClick ? (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onPartTypeClick();
              }
            } : undefined}
            sx={onPartTypeClick ? { cursor: 'pointer' } : undefined}
          >
            <SerialInfo>
              <SerialNumber>{selectedPartType.partTypeName}</SerialNumber>
            </SerialInfo>
          </SelectedSerialChip>
        </SerialSection>
      )}

      {needsOut && (
        <PartSerialSelector
          label="Remove Serial"
          selectedSerial={outSerial}
          onSelect={onOutSelect}
          onClear={onOutClear}
          onSearch={onOutSearch}
          isSearching={isSearching}
          onCreateExceptionally={onCreateOutExceptionally}
        />
      )}

      {needsIn && (
        <PartSerialSelector
          label="Install Serial"
          selectedSerial={inSerial}
          onSelect={onInSelect}
          onClear={onInClear}
          onSearch={onInSearch}
          isSearching={isSearching}
          onCreateExceptionally={onCreateInExceptionally}
        />
      )}

      <NotesSection>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Notes (optional)"
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
          size="small"
        />
      </NotesSection>
    </ConfirmContent>
  );
};
